import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { channelRegistrationService } from '../services/api';
import { updateMetaTags } from '../utils/seo';

const REGISTRATION_TYPES = [
  { key: 'master_distributor', label: 'MASTER DISTRIBUTOR', icon: '🏢' },
  { key: 'super_distributor', label: 'SUPER DISTRIBUTOR', icon: '🚚' },
  { key: 'distributor', label: 'DISTRIBUTOR', icon: '📦' },
  { key: 'retailer_dealer', label: 'RETAILER / DEALER', icon: '🏪' },
  { key: 'franchise', label: 'FRANCHISE', icon: '🤝' },
  { key: 'fofo_foco', label: 'FOFO & FOCO MODEL', icon: '📈' },
];

function ChannelRegistration() {
  const [formConfig, setFormConfig] = useState(null);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [selectedType, setSelectedType] = useState('master_distributor');

  useEffect(() => {
    updateMetaTags({
      title: 'Channel Partner Registration | Ortus FMCG',
      description: 'Register as a Dealer, Distributor, or CNF partner with Ortus FMCG.',
      siteName: 'Ortus FMCG'
    });
    loadForm();
  }, []);

  const loadForm = async () => {
    try {
      const config = await channelRegistrationService.getForm();
      setFormConfig(config);
      // Initialize form data with empty values
      const initial = {};
      (config.fields || []).forEach(f => {
        initial[f.name] = f.type === 'select' && f.options ? '' : '';
      });
      initial.registration_type = 'master_distributor';
      setFormData(initial);
    } catch (e) {
      console.error('Error loading form:', e);
    }
  };

  const getFieldsForType = (type) => {
    if (!formConfig?.fields) return [];
    return formConfig.fields.filter(f => {
      const types = f.types || ['all'];
      return types.includes('all') || types.includes(type);
    });
  };

  const getTypeConfig = (type) => {
    const configs = formConfig?.type_configs || {};
    return configs[type] || { title: '', description: '', icon: '' };
  };

  const handleTypeChange = (type) => {
    setSelectedType(type);
    setFormData(prev => ({ ...prev, registration_type: type }));
    setErrors({});
  };

  const handleChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const validate = () => {
    const errs = {};
    const fields = getFieldsForType(selectedType);
    fields.forEach(field => {
      if (field.required && !formData[field.name]?.toString().trim()) {
        errs[field.name] = `${field.label} is required`;
      }
      if (field.type === 'email' && formData[field.name] && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData[field.name])) {
        errs[field.name] = 'Please enter a valid email address';
      }
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setError('');
    try {
      await channelRegistrationService.submit(formData);
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const apiErrors = err?.response?.data?.errors;
      if (apiErrors) {
        setErrors(apiErrors);
      } else {
        setError(err?.response?.data?.message || 'Failed to submit. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-red-950 to-slate-900 flex items-center justify-center px-4 py-20">
        <div className="max-w-lg w-full bg-white rounded-3xl shadow-2xl p-10 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-3xl font-black text-slate-900 mb-4">Registration Submitted!</h1>
          <p className="text-slate-600 leading-relaxed mb-8">
            Thank you for your interest in becoming a channel partner with Ortus FMCG.
            Our team will review your application and contact you within 48 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/" className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-3 rounded-xl transition shadow-lg hover:scale-105">
              Back to Home
            </Link>
            <button
              onClick={() => { setSuccess(false); setFormData({}); loadForm(); }}
              className="border-2 border-slate-200 hover:border-slate-300 text-slate-700 font-bold px-8 py-3 rounded-xl transition"
            >
              Submit Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!formConfig) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-400">Loading form...</div>
      </div>
    );
  }

  const typeConfig = getTypeConfig(selectedType);
  const visibleFields = getFieldsForType(selectedType);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-red-950 to-slate-900 text-white py-20 md:py-28 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-red-500 opacity-10 blur-3xl rounded-full"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-500 opacity-10 blur-3xl rounded-full"></div>
        <div className="max-w-4xl mx-auto px-4 relative z-10 text-center">
          <div className="h-1 w-20 bg-gradient-to-r from-red-500 to-orange-500 rounded-full mx-auto mb-6"></div>
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="text-red-400 font-bold uppercase tracking-[0.2em] text-xs">Ortus FMCG Network</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight tracking-tight">
            {formConfig.title || 'Channel Partner Registration'}
          </h1>
          <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
            {formConfig.description || 'Join the Ortus FMCG network as a Dealer, Distributor, or CNF partner.'}
          </p>
        </div>
      </section>

      {/* Why Join ORTUS As Channel Partner */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-8">
            <div className="h-1 w-20 bg-gradient-to-r from-red-500 to-orange-500 rounded-full mx-auto mb-4"></div>
            <h2 className="text-2xl md:text-4xl font-black text-slate-900 mb-4 leading-tight">Why Join ORTUS As Channel Partner</h2>
          </div>
          <div className="bg-gradient-to-br from-slate-50 to-red-50/30 rounded-3xl border border-slate-100 p-8 md:p-12">
            <p className="text-slate-700 leading-relaxed text-base md:text-lg">
              At ORTUS, we believe exceptional organizations are built by exceptional people. We are committed to creating an environment where talent thrives, ideas are valued, and performance is recognized. Joining ORTUS means becoming part of a purpose-driven organization that combines entrepreneurial agility with world-class business practices. We empower our people with meaningful responsibilities, continuous learning opportunities, and the freedom to innovate while upholding the highest standards of integrity and excellence.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
            {[
              { icon: '🎯', title: 'Purpose-Driven', desc: 'Be part of an organization that values meaningful impact and entrepreneurial agility.' },
              { icon: '📚', title: 'Continuous Learning', desc: 'We empower our partners with growth opportunities and the freedom to innovate.' },
              { icon: '⭐', title: 'Excellence & Integrity', desc: 'We uphold the highest standards of integrity and excellence in everything we do.' },
            ].map((item) => (
              <div key={item.title} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 text-center">
                <div className="text-3xl mb-3">{item.icon}</div>
                <h3 className="font-bold text-slate-900 text-sm mb-2">{item.title}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4">
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
              {error}
            </div>
          )}

          {/* Type Selection Buttons */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-8">
            {REGISTRATION_TYPES.map(type => {
              const tc = getTypeConfig(type.key);
              const isActive = selectedType === type.key;
              return (
                <button
                  key={type.key}
                  type="button"
                  onClick={() => handleTypeChange(type.key)}
                  className={`relative overflow-hidden rounded-2xl p-4 md:p-6 text-center transition-all duration-300 border-2 ${
                    isActive
                      ? 'bg-gradient-to-br from-red-600 to-orange-600 text-white border-transparent shadow-xl scale-105'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-red-300 hover:shadow-lg'
                  }`}
                >
                  <div className="text-2xl md:text-3xl mb-1.5">{tc.icon || type.icon}</div>
                  <div className={`font-black text-[10px] md:text-xs tracking-wide leading-tight ${isActive ? 'text-white' : 'text-slate-800'}`}>
                    {type.label}
                  </div>
                  {isActive && (
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/30" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Type-specific title and description */}
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-black text-slate-900 mb-2">
              {typeConfig.title || 'Registration Form'}
            </h2>
            <p className="text-slate-500 leading-relaxed max-w-xl mx-auto">
              {typeConfig.description || 'Fill out the form below and our team will get back to you.'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-xl border border-slate-100 p-6 md:p-10">
            <input type="hidden" name="registration_type" value={selectedType} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {visibleFields.map((field) => {
                const isFull = field.width !== 'half' && field.width !== 'third';
                const isThird = field.width === 'third';
                const colSpan = isFull ? 'md:col-span-2' : isThird ? 'md:col-span-2 lg:col-span-1' : '';
                return (
                  <div key={field.name} className={colSpan}>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-0.5">*</span>}
                    </label>
                    {field.type === 'select' ? (
                      <select
                        value={formData[field.name] || ''}
                        onChange={(e) => handleChange(field.name, e.target.value)}
                        className={`w-full bg-slate-50 border rounded-xl px-4 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-red-500 transition ${errors[field.name] ? 'border-red-400' : 'border-slate-200'}`}
                      >
                        <option value="">Select {field.label}</option>
                        {(field.options || []).map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    ) : field.type === 'textarea' ? (
                      <textarea
                        rows={3}
                        value={formData[field.name] || ''}
                        onChange={(e) => handleChange(field.name, e.target.value)}
                        placeholder={`Enter ${field.label.toLowerCase()}`}
                        className={`w-full bg-slate-50 border rounded-xl px-4 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-red-500 transition resize-none ${errors[field.name] ? 'border-red-400' : 'border-slate-200'}`}
                      />
                    ) : (
                      <input
                        type={field.type || 'text'}
                        value={formData[field.name] || ''}
                        onChange={(e) => handleChange(field.name, e.target.value)}
                        placeholder={`Enter ${field.label.toLowerCase()}`}
                        className={`w-full bg-slate-50 border rounded-xl px-4 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-red-500 transition ${errors[field.name] ? 'border-red-400' : 'border-slate-200'}`}
                      />
                    )}
                    {errors[field.name] && (
                      <p className="text-xs text-red-500 mt-1">{errors[field.name]}</p>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 items-center justify-between">
              <p className="text-xs text-slate-400">
                Fields marked with <span className="text-red-500">*</span> are required.
              </p>
              <button
                type="submit"
                disabled={submitting}
                className="w-full sm:w-auto bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-bold px-10 py-3.5 rounded-xl transition shadow-lg hover:scale-105 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting...' : 'Submit Registration'}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

export default ChannelRegistration;
